import {
    PATH_LOCATIONS,
    PATH_STATIONS,
    PATH_CUSTOMERS,
    PATH_CUSTOMER_GROUPS,
    PATH_DASHBOARD,
    PATH_SALES_SESSIONS,
    PATH_TARIFFS,
    PATH_USERS,
    PATH_CONFIGURATION,
    PATH_SYSTEM_LOGS,
    PATH_ACCOUNT_BILLING,
    PATH_SALES_PAYOFF,
    PATH_EMPLOYEE,
} from "src/routes/paths";
import {
    AccountBillingIcon,
    CustomerGroupsIcon,
    CustomersIcon,
    DashboardIcon,
    DiagnosticsIcon,
    EmployeesIcon,
    InvoicesIcon,
    LocationIcon,
    PayoffsIcon,
    SessionsIcon,
    SettingsIcon,
    StationsAltIcon,
    TariffsIcon,
    UsersIcon,
} from "src/theme/overrides/CustomIcons";
import i18next from "src/locales/i18n";
import { UserRoles } from "src/modules/user/types/UserRoles";
import { UserProfilePermissionsModel } from "src/modules/profile/types/UserProfile";
import { CompanyType } from "src/modules/company/types/Company";
import { hasAccess } from "src/routes/permissions";

const ICONS = {
    dashboard: <DashboardIcon sx={{ width: 1, height: 1 }} />,
    sessions: <SessionsIcon sx={{ width: 1, height: 1 }} />,
    invoices: <InvoicesIcon sx={{ width: 1, height: 1 }} />,
    tariffs: <TariffsIcon sx={{ width: 1, height: 1 }} />,
    stations: <StationsAltIcon sx={{ width: 1, height: 1 }} />,
    locations: <LocationIcon sx={{ width: 1, height: 1 }} />,
    customers: <CustomersIcon sx={{ width: 1, height: 1 }} />,
    customerGroups: <CustomerGroupsIcon sx={{ width: 1, height: 1 }} />,
    users: <UsersIcon sx={{ width: 1, height: 1 }} />,
    configuration: <SettingsIcon sx={{ width: 1, height: 1 }} />,
    accountBilling: <AccountBillingIcon sx={{ width: 1, height: 1 }} />,
    systemLogs: <DiagnosticsIcon sx={{ width: 1, height: 1 }} />,
    payoffs: <PayoffsIcon sx={{ width: 1, height: 1 }} />,
    employees: <EmployeesIcon sx={{ width: 1, height: 1 }} />,
};

const getMenuNavigation = (permissions: UserProfilePermissionsModel, hasDefaultTariff: boolean) => {
    if (!permissions.roles && !permissions.hasFullAccess) return [];

    const { hasFullAccess, companyType } = permissions;

    const dashBoardItem = {
        title: i18next.t("shared.layout.nav.dashboard"),
        path: PATH_DASHBOARD,
        icon: ICONS.dashboard,
    };
    const sessionItem = {
        title: i18next.t("shared.layout.nav.sessions"),
        path: PATH_SALES_SESSIONS,
        icon: ICONS.sessions,
    };
    const tariffItem = {
        title: i18next.t("shared.layout.nav.tariffs"),
        path: PATH_TARIFFS,
        icon: ICONS.tariffs,
    };
    const stationItem = {
        title: i18next.t("shared.layout.nav.stations"),
        path: PATH_STATIONS,
        icon: ICONS.stations,
    };
    const locationItem = {
        title: i18next.t("shared.layout.nav.locations"),
        path: PATH_LOCATIONS,
        icon: ICONS.locations,
    };
    const customerItem = {
        title: i18next.t("shared.layout.nav.customers"),
        path: PATH_CUSTOMERS,
        icon: ICONS.customers,
    };
    const customerGroupItem = {
        title: i18next.t("shared.layout.nav.customerGroups"),
        path: PATH_CUSTOMER_GROUPS,
        icon: ICONS.customerGroups,
    };
    const employeesItem = {
        title: i18next.t("shared.layout.nav.employees"),
        path: PATH_EMPLOYEE,
        icon: ICONS.employees,
    };

    const payoffsItem = {
        title: i18next.t("shared.layout.nav.payoffs"),
        path: PATH_SALES_PAYOFF,
        icon: ICONS.payoffs,
    };
    const usersItem = (asCustomers = false) => ({
        title: i18next.t("shared.layout.nav.users"),
        path: PATH_USERS,
        icon: asCustomers ? ICONS.customers : ICONS.users,
    });
    const systemLogsItem = {
        title: i18next.t("shared.layout.nav.systemLogs"),
        path: PATH_SYSTEM_LOGS,
        icon: ICONS.systemLogs,
    };
    const configurationItem = {
        title: i18next.t("shared.layout.nav.configuration"),
        path: PATH_CONFIGURATION,
        icon: ICONS.configuration,
    };
    const planBillingItem = {
        title: i18next.t("shared.layout.nav.accountBilling"),
        path: PATH_ACCOUNT_BILLING,
        icon: ICONS.accountBilling,
    };

    // DASHBOARD
    const navConfig = [{ subheader: "", items: [dashBoardItem] }];

    // CHARGING
    const salesItemsConfig = [];
    const hasSessions = hasAccess(permissions, UserRoles.ROLE_CAN_VIEW_SESSIONS);
    const hasTariffs = hasAccess(permissions, UserRoles.ROLE_CAN_MANAGE_TARIFFS, CompanyType.HOME);
    const hasSales = hasFullAccess || hasSessions || hasTariffs;

    if (hasSessions) salesItemsConfig.push(sessionItem);
    if (hasTariffs) salesItemsConfig.push(tariffItem);

    if (hasSales) {
        navConfig.push({
            subheader: i18next.t("shared.layout.nav.charging"),
            items: salesItemsConfig,
        });
    }

    // ASSETS
    const assetsItemsConfig = [];
    const hasStations = hasAccess(permissions, UserRoles.ROLE_CAN_MANAGE_STATIONS);
    const hasLocations = hasAccess(
        permissions,
        UserRoles.ROLE_CAN_MANAGE_LOCATIONS,
        CompanyType.HOME
    );
    const hasAssets = hasFullAccess || hasStations || hasLocations;

    if (hasStations) assetsItemsConfig.push(stationItem);
    if (hasLocations) assetsItemsConfig.push(locationItem);

    if (hasAssets) {
        navConfig.push({
            subheader: i18next.t("shared.layout.nav.assets"),
            items: assetsItemsConfig,
        });
    }

    // USERS - this is only for home (users)
    if (companyType === CompanyType.HOME) {
        navConfig.push({
            subheader: i18next.t("shared.layout.nav.users"),
            items: [usersItem(true)],
        });
    }

    // CUSTOMERS - this is for everything besides home (recipients + accounts + operations)
    if (companyType !== CompanyType.HOME) {
        let customersItems: any[] = [];

        const hasCustomers = hasAccess(permissions, UserRoles.ROLE_CAN_MANAGE_CUSTOMERS);
        const hasCustomersAccess = hasFullAccess || hasCustomers;
        if (hasDefaultTariff) {
            customersItems = customersItems.concat([customerItem, customerGroupItem]);
        }
        if (hasCustomersAccess) {
            navConfig.push({
                subheader: i18next.t("shared.layout.nav.customers"),
                items: customersItems,
            });
        }
    }

    // EMPLOYEES - for business + fleet
    if (companyType !== CompanyType.HOME) {
        const employeesItems: any[] = [];

        const hasEmployees = hasAccess(permissions, UserRoles.ROLE_CAN_MANAGE_EMPLOYEES);
        const hasPayoffs = hasAccess(permissions, UserRoles.ROLE_CAN_MANAGE_PAYOFFS);
        const hasEmployeesAccess = hasFullAccess || hasEmployees || hasPayoffs;

        if (hasEmployees) employeesItems.push(employeesItem);
        if (hasPayoffs) employeesItems.push(payoffsItem);

        if (hasEmployeesAccess) {
            navConfig.push({
                subheader: i18next.t("shared.layout.nav.employees"),
                items: employeesItems,
            });
        }
    }

    // OPERATIONS - for business + fleet
    if (companyType !== CompanyType.HOME) {
        const operationsItems: any[] = [];

        const hasUsers = hasAccess(permissions, UserRoles.ROLE_CAN_MANAGE_USERS);
        const hasSystemLogs = hasAccess(permissions, UserRoles.ROLE_CAN_ACCESS_SYSTEM_LOGS);
        const hasOperationsAccess = hasFullAccess || hasUsers || hasSystemLogs;

        if (hasUsers) operationsItems.push(usersItem());
        if (hasSystemLogs) operationsItems.push(systemLogsItem);

        if (hasOperationsAccess) {
            navConfig.push({
                subheader: i18next.t("shared.layout.nav.operations"),
                items: operationsItems,
            });
        }
    }

    // SETTINGS
    const settingsItemsConfig = [];

    const hasBilling = hasAccess(permissions, UserRoles.ROLE_CAN_MANAGE_INVOICES);
    const hasSettingAccess = hasFullAccess || hasBilling;

    if (hasBilling) {
        settingsItemsConfig.push(configurationItem);
        settingsItemsConfig.push(planBillingItem);
    }

    if (hasSettingAccess) {
        navConfig.push({
            subheader: i18next.t("shared.layout.nav.settings"),
            items: settingsItemsConfig,
        });
    }

    return navConfig;
};
export default getMenuNavigation;
